import { useLocation } from '@solidjs/router';
import { createSignal, Show, useContext } from 'solid-js';
import { Portal } from 'solid-js/web';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { ShareIosCviIcon } from '../icons-library/share-ios-cvi';
import { SharePopup } from '../share-popup/share-popup';
import { MyContentHandler } from '../my-content-handler/my-content-handler';
import { PdfToolbar } from '../pdf-builder/pdf-toolbar/pdf-toolbar';
import { BlogNewsData } from '../blog-news/blog-news';
import { ProductData } from '../product/product-types';
import { EventData } from '../event/event-types';
import { VideoData } from '../videos/videos-types';
import { Block } from '../../types/shared';
import { EntitySlug, ValidDataTypes } from '../pdf-builder/pdf-store';
import { TabHandler } from '../../tools/tab-handler';

export type ShareAndSaveSupportedPostTypes = 'page' | 'blog-and-news' | 'event' | 'product' | 'video-item';

export type PageData = {
    blocks: Block[];
    title: string;
    permalink: string;
    type: ShareAndSaveSupportedPostTypes;
};

export type PostData = BlogNewsData | ProductData | EventData | VideoData | PageData;

type ShareAndSaveProps = {
    title?: string;
    permalink?: string;
    type?: ShareAndSaveSupportedPostTypes;
    data?: PostData;
};

export const linkItemClass = 'flex items-center gap-cvi-5 py-cvi-2 cursor-pointer text-cvi-fg-default hover:text-cvi-fg-link transition-colors duration-200';

export const ShareAndSave = (props: ShareAndSaveProps) => {
    const { localize, page404 } = useContext(AppContext);
    const [modalVisible, setModalVisible] = createSignal(false);
    const location = useLocation();

    // Accessibility/keyboard-controls related
    const [ firstTabbableElement, setFirstTabbableElement ] = createSignal<HTMLElement | undefined>();
    const [ lastTabbableElement, setLastTabbableElement ] = createSignal<HTMLElement | undefined>();

    const openSharePopup = () => {
        setModalVisible(true);
        document.body.style.overflowY = 'hidden';
    };

    const closeSharePopup = () => {
        setModalVisible(false);
        document.body.style.overflowY = '';
    };

    const shareableContent = () => {
        // It doesn't make sense to share certain pages, like the error page and the personalized content page.
        const nonShareableContent = [
            page404?.url,
        ];

        return !nonShareableContent.includes(location.pathname);
    };

    return (
        <Show when={shareableContent()}>
            <div class="bg-cvi-bg-block-brand px-cvi-8 py-cvi-8 tablet:py-[64px] print:hidden w-full">
                <div class="flex flex-col items-start gap-cvi-7 max-w-cvi-2xl mx-auto tablet:flex-row tablet:flex-wrap tablet:justify-center tablet:items-center tablet:gap-x-[48px] tablet:gap-y-cvi-5">
                    <button class={linkItemClass} onClick={() => openSharePopup()}>
                        <ShareIosCviIcon size="lg" ariaLabel={localize('share', 'Share')} />
                        <span class="body-md font-cvi-regular">
                            {localize('share', 'Share')}
                        </span>
                    </button>

                    <MyContentHandler title={props.title} permalink={props.permalink} type='page' />

                    <PdfToolbar type={props.type as EntitySlug} data={props.data as ValidDataTypes} />
                </div>
            </div>
            {/* DONT REMOVE THIS! as I get hydration error for a second if portal is removed */}
            <Portal mount={document.getElementById('root')!}>
                <Show when={modalVisible()}>
                    <TabHandler refs={{
                        firstTabbableElement,
                        lastTabbableElement,
                    }}>
                        <SharePopup
                            closeModal={() => closeSharePopup()}
                            setFirstTabbableElement={(el: HTMLElement) => setFirstTabbableElement(el)}
                            setLastTabbableElement={(el: HTMLElement) => setLastTabbableElement(el)}
                        />
                    </TabHandler>
                </Show>
            </Portal>
        </Show>
    );
};
